import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { State } from "../base/types";
import {
  confirmOrder as confirmOrderApi,
  getOrders as getOrdersApi,
  getRequisiteOrders as getRequisiteOrdersApi,
  Order,
  OrderForm,
  OrderResponse,
  RequisiteOrdersForm,
  RequisiteOrdersResponse,
} from "./api";

type OrdersState = State<{
  orders: Order[] | null;
  requisiteOrders: Order[] | null;
  page: number;
  totalCount: number;
  requisiteOrdersTotalCount: number;
  requisiteOrdersLoading: boolean;
}>;

export const getOrders = createAsyncThunk<
  OrderResponse,
  { userId: string; data: OrderForm }
>("orders/getOrders", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await getOrdersApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getRequisiteOrders = createAsyncThunk<
  RequisiteOrdersResponse,
  { requisiteId: string; data: RequisiteOrdersForm }
>(
  "orders/getRequisiteOrders",
  async ({ requisiteId, data }, { rejectWithValue }) => {
    try {
      return await getRequisiteOrdersApi(requisiteId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const confirmOrder = createAsyncThunk<
  string,
  { userId: string; orderId: string }
>("orders/confirm", async ({ userId, orderId }, { rejectWithValue }) => {
  try {
    return await confirmOrderApi(userId, orderId);
  } catch (error) {
    return rejectWithValue(error);
  }
});


const initialState: OrdersState = {
  orders: null,
  requisiteOrders: null,
  loading: false,
  requisiteOrdersLoading: false,
  error: null,
  page: 1,
  totalCount: 0,
  requisiteOrdersTotalCount: 0,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getRequisiteOrders.pending, (state) => {
        state.requisiteOrdersLoading = true;
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.user_order;
        state.page = action.payload.page;
        state.totalCount = action.payload.count;
      })
      .addCase(getRequisiteOrders.fulfilled, (state, action) => {
        state.requisiteOrdersLoading = false;
        state.requisiteOrders = action.payload.orders;
        state.requisiteOrdersTotalCount = action.payload.total_count;
      })
      .addCase(confirmOrder.fulfilled, (state, action) => {
        const orderId = action.meta.arg.orderId;
        state.requisiteOrdersLoading = false;
        const confirmedOrder = state.orders?.find(
          (order) => order.id === orderId
        );

        if (confirmedOrder) {
          confirmedOrder.status = "completed";
        }
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default ordersSlice.reducer;
